import type { ContentTypeObject, CustomHtmlElement } from '../../types/pbTypes';
import type { PbBanner, PbHeroBanner } from '../../types/pbBlocks';
import {
    getBackgroundImages,
    getBackgroundImagesForLozad,
    getBorder,
    getPadding,
    getTextAlign,
    parseStyleString,
    getButtonLinkType,
    getButtonType,
    getCssClasses,
    getMargin,
    getIsHidden,
    getVideoOptions,
} from '../utils';

export type BannerType = ContentTypeObject & PbBanner;
export type HeroType = ContentTypeObject & PbHeroBanner;

/**
 * Aggregates the configuration of a banner element.
 * @param node
 * @param props
 */
export function bannerConfigAggregator(node: CustomHtmlElement, props?: any): BannerType | HeroType {
    const style = parseStyleString(node?.attributes.style);

    const wrapperElement = node.querySelector('[data-element="wrapper"]') as unknown as CustomHtmlElement | null;
    const wrapperStyles = parseStyleString(wrapperElement?.attributes.style);

    const overlayElement = node.querySelector('[data-element="overlay"]') as unknown as CustomHtmlElement | null;
    const overlayStyles = parseStyleString(overlayElement?.attributes.style);

    const linkElement = node.querySelector('a[data-element="link"]') as unknown as CustomHtmlElement | null;
    const buttonElement = node.querySelector('[data-element="button"]') as unknown as CustomHtmlElement | null;

    const contentElement = node.querySelector('[data-element="content"]') as unknown as CustomHtmlElement | null;

    const buttonBackgroundColour = node.attributes['background_colour'];
    const buttonTextColour = node.attributes['text_colour'];
    const showButton = node.attributes['data-show-button'];
    const showOverlay = node.attributes['data-show-overlay'];

    const content = contentElement?.innerHTML;

    const isPoster = props.appearance === 'poster';
    const isHero = props.appearance === 'hero';
    const contentPosition = node.attributes['data-align'] ?? 'centre';
    const actions = node.attributes['data-links'] ? JSON.parse(node.attributes['data-links']) : [];
    const backgroundImages = getBackgroundImagesForLozad(wrapperElement);

    const shared = {
        ...getCssClasses(node),
        ...getMargin(style),
        ...getIsHidden(style),
        ...getTextAlign(wrapperStyles),
        ...getBorder(wrapperStyles),
        ...getPadding(isPoster ? overlayStyles : wrapperStyles),
        ...getVideoOptions(wrapperElement),
        // Add the images as separate properties for lozad
        desktopImage: backgroundImages.desktopImage,
        mobileImage: backgroundImages.mobileImage,
        altText: overlayElement?.attributes['aria-label'],
        backgroundType: wrapperElement?.attributes['data-background-type'],
        buttonBackgroundColour,
        buttonText: buttonElement?.textContent,
        buttonTextColour,
        content: content,
        openInNewTab: linkElement?.attributes['target'] === '_blank',
        titleText: overlayElement?.attributes['title'],
        uniqueID: node.attributes['data-pb-style'],
    };

    const hero = {
        actions: Array.isArray(actions) ? actions : [],
        backgroundColor: wrapperStyles?.backgroundColor,
        contentPosition,
        hasMoreThanOneSlide: node.parentNode.childNodes.length > 1,
        laptopImage: wrapperElement?.attributes['data-laptop-image-src'],
        ...shared,
    };

    const banner = {
        ...getBackgroundImages(wrapperElement, wrapperStyles),
        buttonType: getButtonType(buttonElement?.classNames),
        link: linkElement?.attributes['href'],
        linkType: getButtonLinkType(linkElement?.attributes['data-link-type']),
        minHeight: isPoster ? overlayStyles?.minHeight : wrapperStyles?.minHeight,
        overlayColor: overlayElement?.attributes['data-overlay-color'],
        showButton,
        showOverlay,
        ...shared,
    };

    if (isHero) {
        return hero;
    }

    return banner;
}
